import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { ChevronRight } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface ContentDropdownProps {
  title: string;
  icon: React.ElementType;
  isActive?: boolean;
  url: string;
  onNavigate: (url: string) => void;
  children: React.ReactNode;
  onOpen?: () => void;
  action?: React.ReactNode;
}

export function ContentDropdown({
  title,
  icon: Icon,
  isActive,
  url,
  onNavigate,
  children,
  onOpen,
  action
}: ContentDropdownProps) {
  // Start open if the section is active
  const [isOpen, setIsOpen] = useState(isActive);
  const [hasOpened, setHasOpened] = useState(isActive || false);

  // Call onOpen when the dropdown is opened for the first time
  useEffect(() => {
    if (isOpen && !hasOpened && onOpen) {
      setHasOpened(true);
      onOpen();
    }
  }, [isOpen, hasOpened, onOpen]);

  // Update open state if active state changes externally
  useEffect(() => {
    if (isActive) {
      setIsOpen(true);
    }
  }, [isActive]);

  const handleTitleClick = (e: React.MouseEvent) => {
    // Prevent default navigation
    e.preventDefault();

    // Toggle the dropdown without navigating
    setIsOpen(!isOpen);
  };

  return (
    <Collapsible
      asChild
      open={isOpen}
      onOpenChange={setIsOpen}
      className="group/collapsible"
    >
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            onClick={handleTitleClick}
            tooltip={title}
            className={cn(
              "h-9 px-3 transition-all duration-300 ease-in-out mb-1.5",
              "group-data-[collapsible=icon]:!h-9 group-data-[collapsible=icon]:!w-9 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:!rounded-md",
              isActive
                ? "bg-sidebar-accent text-sidebar-accent-foreground font-semibold shadow-sm rounded-2xl"
                : "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 rounded-2xl"
            )}
            isActive={false} // Force false to prevent default sidebar active styles to control it manually
          >
            <Icon className={cn("size-4", isActive ? "text-sidebar-accent-foreground" : "text-sidebar-foreground/70 group-hover:text-sidebar-foreground")} />
            <span className="text-sm group-data-[collapsible=icon]:hidden">{title}</span>

            <ChevronRight className="ml-auto size-3.5 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 text-sidebar-foreground/50 group-data-[collapsible=icon]:hidden" />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        {action}
        <CollapsibleContent>
          <SidebarMenuSub className="py-0 mt-0 gap-0 border-l-0 pl-0">
            {children}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}
